import { Add as AddIcon, ExpandMore } from '@mui/icons-material'
import {
  Box,
  Button,
  ClickAwayListener,
  Collapse,
  Fab,
  Grow,
  useScrollTrigger,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import React, { useCallback, useEffect, useState } from 'react'
import { ButtonLink } from '@/helpers/compositions/ButtonLink'
import { T } from '@/helpers/T'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { useDisplayMode } from '@/hooks/useDisplayMode'
import { useIntlRich } from '@/hooks/useIntlRich'
import { useNavigateGoBack } from '@/hooks/useNavigateGoBack'
import { useScrollable } from '@/hooks/useScrollable'
import { useViewDomRect } from '@/hooks/useViewDomRect'
import { useViewContext } from '@/providers/Global'

import { useQuickActionsMenu } from './useQuickActionsMenu'

//	///////////////////////
//	MARK: TYPES
export interface ICQuickActionsProps {}

//	///////////////////////
//	MARK: EXPORTS
const QuickActionsInternal: React.FC<ICQuickActionsProps> = (props) => {
  const theme = useTheme()
  const intl = useIntlRich()
  const View = useViewContext()
  const navigateGoBack = useNavigateGoBack()
  const displayMode = useDisplayMode()

  const [isDesktop] = useBreakpoint('md')

  const [setRef, rect] = useViewDomRect('quickActions')
  const [setRefAdd, rectAdd, refAdd] = useViewDomRect('quickActionsAdd')

  const actions = useQuickActionsMenu(isDesktop)
  const [actionShowed, setActionStatus] = useState<boolean>(false)
  const toggleActions = useCallback(() => setActionStatus((v) => !v), [])

  const closeActions = useCallback(() => setActionStatus(false), [])

  const closeActionsAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (
        event.target !== refAdd &&
        !refAdd?.contains(event.target as Node | null) &&
        !View.navbar.ref?.contains(event.target as Node | null) &&
        !View.subNavbar.ref?.contains(event.target as Node | null)
      ) {
        setActionStatus(false)
      }
    },
    [View.navbar.ref, View.subNavbar.ref, refAdd],
  )

  useEffect(() => {
    if (actionShowed && actions.length === 0) {
      setActionStatus(false)
    }
  }, [actionShowed, actions.length])

  const scrollable = useScrollable()
  const scrolled = useScrollTrigger({
    threshold: 0,
  })

  const scroll = useCallback(() => {
    window.scrollTo(
      scrolled
        ? {
            top: 0,
          }
        : {
            top: 9_999_999_999_999,
          },
    )
  }, [scrolled])

  return null

  // biome-ignore lint/correctness/noUnreachable: disabled for now
  return (
    <>
      <Box
        ref={isDesktop ? setRefAdd : undefined}
        sx={{
          position: 'fixed',
          top: `${View.navbar.height + View.subNavbar.height}px`,
          right: '24px',
          transform: 'translateY(-50%)',
          zIndex: theme.zIndex.appBar,
          pointerEvents: 'none',
        }}
      >
        <Grow appear in={isDesktop && actions.length > 0} easing='ease'>
          <Button
            size='large'
            startIcon={<AddIcon />}
            onClick={toggleActions}
            sx={{
              borderRadius: '999px',
              whiteSpace: 'nowrap',
              pointerEvents: 'auto',
              boxShadow: 2,
            }}
          >
            <T id='lang.quick_add' />
          </Button>
        </Grow>
      </Box>
      <Box
        ref={setRef}
        display='flex'
        sx={{
          zIndex: theme.zIndex.appBar,
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: isDesktop ? View.bottomBar.bottom : 0,
          pointerEvents: 'none',
        }}
      >
        <div>
          <Grow
            appear={true}
            in={window.history.length > 1 && displayMode === 'fullscreen'}
            easing='ease'
            style={{ transformOrigin: 'bottom center' }}
          >
            <Box p={2} pr={2} pl={1}>
              <Fab
                color='inherit'
                aria-label={intl.formatMessageTyped({ id: 'lang.back' })}
                onClick={navigateGoBack}
                sx={{ pointerEvents: 'auto', boxShadow: 2 }}
              >
                <ExpandMore
                  sx={{
                    transition: 'all ease .3s',
                    transform: 'rotate(90deg)',
                  }}
                />
              </Fab>
            </Box>
          </Grow>
        </div>
        <Box display='flex' ml='auto'>
          <Grow
            appear={true}
            in={scrollable}
            easing='ease'
            style={{ transformOrigin: 'bottom center' }}
          >
            <Box p={2} pr={2} pl={1}>
              <Fab
                color='inherit'
                aria-label={
                  scrolled
                    ? intl.formatMessageTyped({ id: 'lang.scroll_to_top' })
                    : intl.formatMessageTyped({ id: 'lang.scroll_to_bottom' })
                }
                onClick={scroll}
                sx={{ pointerEvents: 'auto', boxShadow: 2 }}
              >
                <ExpandMore
                  sx={{
                    transition: 'all ease .3s',
                    transform: scrolled ? 'scaleY(-1)' : 'scale(1)',
                  }}
                />
              </Fab>
            </Box>
          </Grow>
          <Collapse
            ref={isDesktop ? undefined : setRefAdd}
            appear
            in={!isDesktop && actions.length > 0}
            easing='ease'
            orientation='horizontal'
            sx={{ ml: 'auto' }}
          >
            <Box p={2} pl={1}>
              <Fab
                color='inherit'
                aria-label={intl.formatMessageTyped({ id: 'lang.quick_add' })}
                onClick={toggleActions}
                sx={{ pointerEvents: 'auto' }}
              >
                <AddIcon />
              </Fab>
            </Box>
          </Collapse>
        </Box>
      </Box>
      <ClickAwayListener onClickAway={closeActionsAway}>
        <Box
          sx={(t) => ({
            zIndex: theme.zIndex.appBar,
            position: 'fixed',
            right: isDesktop ? t.spacing(3) : t.spacing(2),
            top: isDesktop
              ? View.navbar.height + View.subNavbar.height + rectAdd.height / 2 + 8
              : undefined,
            bottom: isDesktop ? undefined : rect.height,
            '& > *': {
              display: 'block',
            },
            pointerEvents: 'none',
          })}
        >
          {actions.map(({ key, content: Content }, i, arr) => (
            <Grow
              key={key}
              in={actionShowed}
              style={{
                transitionDelay: `${(isDesktop ? i : arr.length - 1 - i) * 30}ms`,
                transformOrigin: 'bottom right',
              }}
            >
              <Box textAlign='right' mt={0.5} onClick={closeActions}>
                {typeof Content === 'function' ? (
                  <Content />
                ) : (
                  // @ts-expect-error
                  <ButtonLink {...Content} sx={{ pointerEvents: 'auto' }} />
                )}
              </Box>
            </Grow>
          ))}
        </Box>
      </ClickAwayListener>
    </>
  )
}

export const QuickActions = React.memo(QuickActionsInternal) as typeof QuickActionsInternal
